/**
 * URL utility functions for handling encoded URLs
 */

/**
 * Decode a URL, handling double-encoding
 * @param {string} url - The URL to decode
 * @returns {string} - The decoded URL
 */
export function decodeImageUrl(url) {
  if (!url || typeof url !== 'string') {
    return url;
  }

  try {
    // Try decoding multiple times to handle double/triple encoding
    let decoded = url;
    let previous = '';
    
    // Keep decoding until no more changes occur
    while (decoded !== previous) {
      previous = decoded;
      try {
        decoded = decodeURIComponent(decoded);
      } catch (e) {
        // If decoding fails, try decodeURI
        try {
          decoded = decodeURI(decoded);
        } catch (e2) {
          // If both fail, return the last successful decode
          break;
        }
      }
    }
    
    return decoded;
  } catch (e) {
    // If all decoding fails, return original
    return url;
  }
}

/**
 * Normalize image URL - decode and fix common issues
 * @param {string} url - The image URL
 * @returns {string} - The normalized URL
 */
export function normalizeImageUrl(url) {
  if (!url || typeof url !== 'string') {
    return null;
  }

  // Decode the URL
  let normalized = decodeImageUrl(url);

  // Fix double-encoded characters (e.g., %2520 -> %20 -> space)
  // This handles cases where URLs are encoded multiple times
  normalized = normalized.replace(/%25([0-9A-F]{2})/gi, '%$1');

  // Fix Wikimedia Commons URLs that might be malformed
  if (normalized.includes('wikimedia.org') || normalized.includes('Special:FilePath')) {
    // For Wikimedia Commons, we need to use the direct file URL
    // Special:FilePath URLs need to be converted to direct file URLs
    if (normalized.includes('Special:FilePath')) {
      // Extract filename from Special:FilePath URL
      const match = normalized.match(/Special:FilePath\/(.+?)(?:\?|$)/);
      if (match) {
        const filename = decodeImageUrl(match[1]);
        // Use the direct file URL format
        normalized = `https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(filename)}`;
      }
    }
  }

  return normalized;
}

/**
 * Check if URL is valid and accessible
 * @param {string} url - The URL to check
 * @returns {Promise<boolean>} - True if URL is accessible
 */
export async function isValidImageUrl(url) {
  if (!url) return false;
  
  try {
    await fetch(url, { method: 'HEAD', mode: 'no-cors' });
    return true;
  } catch (e) {
    return false;
  }
}

